import { api } from './api';

export type DocumentStatus =
  | 'pending'
  | 'parsing'
  | 'chunking'
  | 'indexing'
  | 'indexed'
  | 'failed'
  | 'tombstoned';

export interface DocumentAccess {
  documentId: string;
  title: string;
  sourceKind: string;
  totalMembers: number;
  allowedCount: number;
  members: { userId: string; name: string | null; email: string | null; role: string; canAccess: boolean }[];
}

export interface DocumentSummary {
  id: string;
  title: string;
  sourceKind: string;
  mimeType: string | null;
  language: string | null;
  sourceUrl: string | null;
  chunkCount: number;
  ocrKind: string | null;
  status: DocumentStatus;
  error: string | null;
  byteSize: number | null;
  ingestedAt: string | null;
  updatedAt: string;
}

export const documentsApi = {
  async list(wsId: string): Promise<DocumentSummary[]> {
    const r = await api.get<DocumentSummary[]>(`/api/workspaces/${wsId}/documents`);
    return r.data;
  },
  async get(wsId: string, id: string): Promise<DocumentSummary> {
    const r = await api.get<DocumentSummary>(`/api/workspaces/${wsId}/documents/${id}`);
    return r.data;
  },
  /** Delete a document everywhere (Postgres + Qdrant + lexical + blob). */
  async remove(wsId: string, id: string): Promise<{ ok: boolean }> {
    const r = await api.delete<{ ok: boolean }>(`/api/workspaces/${wsId}/documents/${id}`);
    return r.data;
  },
  /** Re-run the ingest pipeline for a document (e.g. a failed one). */
  async reindex(wsId: string, id: string): Promise<DocumentSummary> {
    const r = await api.post<DocumentSummary>(`/api/workspaces/${wsId}/documents/${id}/reindex`);
    return r.data;
  },
  /** One-click sample data — ingests the demo corpus through the real pipeline. */
  async seedDemo(wsId: string): Promise<{ seeded: number; alreadyPresent: boolean }> {
    const r = await api.post<{ seeded: number; alreadyPresent: boolean }>(`/api/workspaces/${wsId}/documents/seed-demo`);
    return r.data;
  },
  /** "Who can find this document?" — provable per-member access. */
  async access(wsId: string, id: string): Promise<DocumentAccess> {
    const r = await api.get<DocumentAccess>(`/api/workspaces/${wsId}/documents/${id}/access`);
    return r.data;
  },
  /** Purge EVERY document in the workspace (destructive). */
  async purgeWorkspace(wsId: string): Promise<{ deleted: number }> {
    const r = await api.delete<{ deleted: number }>(`/api/workspaces/${wsId}/documents`, { params: { confirm: 'PURGE' } });
    return r.data;
  },
  /** Re-ingest every document in the workspace (recovery / model migration). */
  async reindexWorkspace(wsId: string): Promise<{ queued: number }> {
    const r = await api.post<{ queued: number }>(`/api/workspaces/${wsId}/reindex`);
    return r.data;
  },
  /** Index stats: doc/chunk/vector counts. */
  async indexStats(wsId: string): Promise<{ documents: number; indexed: number; failed: number; chunks: number; vectors: number }> {
    const r = await api.get<{ documents: number; indexed: number; failed: number; chunks: number; vectors: number }>(`/api/workspaces/${wsId}/index/stats`);
    return r.data;
  },
  /** Documents similar to a given one (Qdrant recommend). */
  async findSimilar(wsId: string, id: string): Promise<Array<DocumentSummary & { score: number }>> {
    const r = await api.get<Array<DocumentSummary & { score: number }>>(`/api/workspaces/${wsId}/documents/${id}/similar`);
    return r.data;
  },
  async upload(
    wsId: string,
    file: File,
    languageHint?: 'en' | 'ja' | 'auto',
  ): Promise<DocumentSummary> {
    const fd = new FormData();
    fd.append('file', file);
    if (languageHint) fd.append('languageHint', languageHint);
    const r = await api.post<DocumentSummary>(
      `/api/workspaces/${wsId}/documents/upload`,
      fd,
    );
    return r.data;
  },
  async bulkUpload(
    wsId: string,
    files: File[],
    languageHint?: 'en' | 'ja' | 'auto',
  ): Promise<BulkUploadResponse> {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    if (languageHint) fd.append('languageHint', languageHint);
    const r = await api.post<BulkUploadResponse>(
      `/api/workspaces/${wsId}/documents/bulk`,
      fd,
    );
    return r.data;
  },
};

export interface BulkUploadResponse {
  total: number;
  accepted: number;
  failed: number;
  results: { originalname: string; documentId: string | null; error?: string }[];
}
